/// <amd-module name="Build/Admin/Common/Actions/ServiceEndpointActionCreator" />
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

import { AzureSubscription, ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";

import { NotificationActionCreator } from "Build/Admin/Common/Actions/NotificationActionCreator";
import { ServiceEndpointActions } from "Build/Admin/Common/Actions/ServiceEndpointActions";
import { VirtualMachineScaleSet } from "Build/Admin/Common/Client/Contracts/DistributedTask/Azure";
import { Telemetry, StoreStatus } from "Build/Admin/Common/Constants";
import { ServiceEndpointSource } from "Build/Admin/Common/Sources/ServiceEndpointSource";
import { NotificationType } from "Build/Admin/Common/Stores/NotificationStore";
import { ServiceEndpointStore } from "Build/Admin/Common/Stores/ServiceEndpointStore";

export interface IServiceEndpointActionCreatorOptions {
    pageContext: IVssPageContext;
    notificationActionCreator: NotificationActionCreator;
    serviceEndpointActions: ServiceEndpointActions;
    serviceEndpointSource: ServiceEndpointSource;
    serviceEndpointStore: ServiceEndpointStore;
}

export class ServiceEndpointActionCreator {
    private _pageContext: IVssPageContext;
    private _notificationActionCreator: NotificationActionCreator;
    private _serviceEndpointActions: ServiceEndpointActions;
    private _serviceEndpointSource: ServiceEndpointSource;
    private _serviceEndpointStore: ServiceEndpointStore;

    constructor(options: IServiceEndpointActionCreatorOptions) {
        this._pageContext = options.pageContext;
        this._notificationActionCreator = options.notificationActionCreator;
        this._serviceEndpointActions = options.serviceEndpointActions;
        this._serviceEndpointSource = options.serviceEndpointSource;
        this._serviceEndpointStore = options.serviceEndpointStore;
    }

    public loadAzureSubscriptions(forceReload: boolean = false): void {
        if (forceReload ||
            this._serviceEndpointStore.state.azureSubscriptionsStatus === StoreStatus.None) {
            this._serviceEndpointActions.azureSubscriptionsStatusUpdated.invoke(StoreStatus.Loading);
            this._serviceEndpointSource.getAzureSubscriptions().then(
                (subscriptions: AzureSubscription[]) => {
                    this._serviceEndpointActions.azureSubscriptionsUpdated.invoke(subscriptions);
                },
                (reason) => {
                    this._handleError(reason);
                }
            );
        }
    }

    public loadServiceEndpoints(projectId: string, forceReload: boolean = false): void {
        if (forceReload ||
            this._serviceEndpointStore.state.serviceEndpointsStatus === StoreStatus.None ||
            this._serviceEndpointStore.state.serviceEndpointScope !== projectId) {
            this._serviceEndpointActions.serviceEndpointsStatusUpdated.invoke(StoreStatus.Loading);
            this._serviceEndpointSource.getServiceEndpoints(projectId).then(
                (serviceEndpoints: ServiceEndpoint[]) => {
                    this._serviceEndpointActions.serviceEndpointsUpdated.invoke({serviceEndpointScope: projectId, serviceEndpoints});
                },
                (reason) => {
                    this._handleError(reason);
                }
            );
        }
    }

    public authorizeNewServiceEndpoint(projectId: string, azureSubscription: AzureSubscription): string {
        const subscriptionCopy: AzureSubscription = {...azureSubscription};
        let connectionName: string = this.getSubscriptionDisplayName(subscriptionCopy);
        let connectionNameNumber: number = 1;
        const currentConnectionNames: string[] = this._serviceEndpointStore.state.serviceEndpoints.map(e => e.name);
        while (currentConnectionNames.indexOf(connectionName) >= 0) {
            subscriptionCopy.displayName = azureSubscription.displayName + '(' + connectionNameNumber + ')';
            connectionName = this.getSubscriptionDisplayName(subscriptionCopy);
            connectionNameNumber++;
        }

        this._serviceEndpointSource.createAzureRMConnection(projectId, subscriptionCopy).then(
            (serviceEndpoint: ServiceEndpoint) => {
                this._serviceEndpointActions.serviceEndpointAuthorized.invoke(serviceEndpoint);
                this.loadVirtualMachineScaleSets(projectId, serviceEndpoint);
            },
            (reason) => {
                this._handleError(reason);
            }
        );

        return connectionName;
    }

    public loadVirtualMachineScaleSets(projectId: string, serviceEndpoint: ServiceEndpoint): void {
        this._serviceEndpointActions.virtualMachineScaleSetsUpdated.invoke([]);
        this._serviceEndpointActions.virtualMachineScaleSetsStatusUpdated.invoke(StoreStatus.Loading);
        this._serviceEndpointSource.getVirtualMachineScaleSetsAsync(projectId, serviceEndpoint).then(
            (scaleSets: VirtualMachineScaleSet[]) => {
                this._serviceEndpointActions.virtualMachineScaleSetsUpdated.invoke(scaleSets);
            },
            (reason) => {
                if (reason && reason.message) {
                    this._serviceEndpointActions.virtualMachineScaleSetsUpdated.invoke([]);
                    this._serviceEndpointActions.virtualMachineScaleSetsErrorUpdated.invoke(reason.message);
                    const service = this._pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
                    service.publishEvent(Telemetry.Area, Telemetry.PoolsAdminFeature, {
                        message: reason.message,
                        error: reason
                    });
                }
            }
        );
    }

    public getSubscriptionDisplayName(subscription: AzureSubscription): string {
        return subscription.displayName + '(' + subscription.subscriptionId + ')';
    }

    // tslint:disable-next-line:no-any
    private _handleError(reason: any): void {
        if (reason && reason.message) {
            this._notificationActionCreator.addNotification(reason.message, NotificationType.error);
            const service = this._pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
            service.publishEvent(Telemetry.Area, Telemetry.PoolsAdminFeature, {
                message: reason.message,
                error: reason
            });
        }
    }
}
